import { ref } from 'vue'
import { N8N_TEAMS_URL, N8N_SESSIONS_URL } from '@/api/n8n'

const WEBHOOK = N8N_TEAMS_URL

async function postTo(url: string, command: string, extra: Record<string, unknown> = {}): Promise<unknown> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ command, timestamp: Date.now(), ...extra }),
  })
  if (!res.ok) throw new Error(`Admin DB ${res.status}`)
  // Alguns comandos (ex.: list_* sem nenhuma linha) voltam com corpo vazio em
  // vez de "[]" — res.json() quebra nesse caso ("Unexpected end of JSON
  // input"). Trata corpo vazio como "sem dados" em vez de propagar o erro.
  const text = await res.text()
  if (!text.trim()) return []
  return JSON.parse(text)
}

async function post(command: string, extra: Record<string, unknown> = {}): Promise<unknown> {
  return postTo(WEBHOOK, command, extra)
}

// Sessões (Admin → Sessões, migração v30) vivem num workflow n8n ISOLADO
// (n8n/Sessoes-Agentes.json), não no webhook gigante compartilhado — mesmo
// motivo do call_conv (ver callConvService.ts): heartbeat roda a cada 30s por
// ABA aberta, e jogar isso no Switch de ~30 comandos só aumentaria a disputa
// que o PLANO-OTIMIZACAO-CARREGAMENTO-DASHBOARD.md (achado 1) já mapeou.
async function postSessions(command: string, extra: Record<string, unknown> = {}): Promise<unknown> {
  return postTo(N8N_SESSIONS_URL, command, extra)
}

export interface DBProfile {
  id: string
  name: string
  color: string
  team_ids: number[]
  user_ids: number[]
  permissions: Record<string, boolean>
  is_protected: boolean
}

export interface CWTeam {
  id: number
  name: string
  description: string
  agents_count?: number
}

export interface CWAgent {
  id: number
  name: string
  email: string
  role: string
  availability_status: string
  avatar_url?: string
}

export interface DBAgentCategory {
  team_id: number
  user_id: number
  categories: string[]
  fluxo_ids: number[]
}

export interface TeamFallbackAgentRow {
  team_id: number
  user_id: number
  categories: string[]
  fluxo_ids: number[]
}

export interface LastAccessRow {
  user_id: number
  last_access: number // epoch seconds
}

export interface AgentSessionRow {
  session_id:   string
  user_id:      number
  email:        string
  name:         string
  device:       string
  created_at:   number // epoch seconds
  last_seen_at: number // epoch seconds
  revoked:      boolean
}

export interface AgentStatusRow {
  user_id: number
  availability: string
}

export interface BlacklistRow {
  contact_id: number
  name: string
  phone: string
  email: string
  reason_label: string
  context_team: string
  added_at: number // epoch seconds
}

function parseJsonField<T>(v: T | string | null | undefined, fallback: T): T {
  if (v === null || v === undefined) return fallback
  if (typeof v === 'string') { try { return JSON.parse(v) } catch { return fallback } }
  return v
}

function rowToProfile(row: Record<string, unknown>): DBProfile {
  return {
    id:           String(row.id),
    name:         String(row.name),
    color:        String(row.color || '#BC0C24'),
    team_ids:     parseJsonField<number[]>(row.team_ids as string | number[], []),
    user_ids:     parseJsonField<number[]>(row.user_ids as string | number[], []),
    permissions:  parseJsonField<Record<string, boolean>>(row.permissions as string | Record<string, boolean>, {}),
    is_protected: Number(row.is_protected) === 1,
  }
}

const dbLoading = ref(false)
const dbError   = ref('')

export function useAdminDB() {

  // ── Profiles ─────────────────────────────────────────────────
  async function loadProfiles(): Promise<DBProfile[]> {
    const data = await post('get_profiles')
    const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
    return rows.map(rowToProfile)
  }

  async function createProfile(p: DBProfile): Promise<void> {
    await post('create_profile', {
      profile: {
        id:           p.id,
        name:         p.name,
        color:        p.color,
        team_ids:     p.team_ids,
        user_ids:     p.user_ids,
        permissions:  p.permissions,
        is_protected: p.is_protected ? 1 : 0,
      },
    })
  }

  async function updateProfile(p: DBProfile): Promise<void> {
    await post('update_profile', {
      profile: {
        id:          p.id,
        name:        p.name,
        color:       p.color,
        team_ids:    p.team_ids,
        user_ids:    p.user_ids,
        permissions: p.permissions,
      },
    })
  }

  async function deleteProfile(id: string): Promise<void> {
    await post('delete_profile', { profile_id: id })
  }

  // ── Settings ─────────────────────────────────────────────────
  async function loadSettings(): Promise<Record<string, string>> {
    const data = await post('get_settings')
    const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
    const out: Record<string, string> = {}
    rows.forEach(r => { out[String(r.setting_key)] = String(r.setting_value) })
    return out
  }

  async function updateSetting(key: string, value: string): Promise<void> {
    await post('update_settings', { setting_key: key, setting_value: value })
  }

  // ── Times (Chatwoot API via n8n) ──────────────────────────────
  async function createTeam(team: { name: string; description?: string }): Promise<CWTeam> {
    const data = await post('create_team', { team }) as CWTeam | CWTeam[]
    return Array.isArray(data) ? data[0] : data
  }

  async function updateTeam(teamId: number, team: { name?: string; description?: string }): Promise<CWTeam> {
    const data = await post('update_team', { team_id: teamId, team }) as CWTeam | CWTeam[]
    return Array.isArray(data) ? data[0] : data
  }

  async function deleteTeam(teamId: number): Promise<void> {
    await post('delete_team', { team_id: teamId })
  }

  async function addTeamAgent(teamId: number, userIds: number[]): Promise<void> {
    await post('add_team_agent', { team_id: teamId, user_ids: userIds })
  }

  async function removeTeamAgent(teamId: number, userIds: number[]): Promise<void> {
    await post('remove_team_agent', { team_id: teamId, user_ids: userIds })
  }

  // ── Agentes (Chatwoot API via n8n) ────────────────────────────
  async function createAgent(agent: { name: string; email: string; role: string; availability_status?: string }): Promise<CWAgent> {
    const data = await post('create_agent', { agent }) as CWAgent | CWAgent[]
    return Array.isArray(data) ? data[0] : data
  }

  async function updateAgent(agentId: number, agent: { name?: string; availability_status?: string; role?: string }): Promise<CWAgent> {
    const data = await post('update_agent', { agent_id: agentId, agent }) as CWAgent | CWAgent[]
    return Array.isArray(data) ? data[0] : data
  }

  async function deleteAgent(agentId: number): Promise<void> {
    await post('delete_agent', { agent_id: agentId })
  }

  // ── Categorias + Fluxos de Agente por Time (Aprendizagem/Comercial/
  // Financeiro × Geral/Empresa Cliente/.../Candidato — migração v22) ──
  async function loadAgentCategories(): Promise<DBAgentCategory[]> {
    const data = await post('get_agent_categories')
    const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
    return rows.map(r => ({
      team_id:    Number(r.team_id),
      user_id:    Number(r.user_id),
      categories: parseJsonField<string[]>(r.categories as string | string[], []),
      fluxo_ids:  parseJsonField<number[]>(r.fluxo_ids as string | number[], []),
    }))
  }

  async function setAgentCategory(teamId: number, userId: number, categories: string[], fluxoIds: number[] = []): Promise<void> {
    await post('set_agent_category', { team_id: teamId, user_id: userId, categories, fluxo_ids: fluxoIds })
  }

  // ── Agentes de Fallback por Time (perfil "EC - ISBET" — migração v22) ──
  // Fila separada de agent_categories: aqui o agente não é necessariamente
  // membro do time no Chatwoot — é só quem cobre esse time quando a unidade
  // não consegue atender (ver TeamsView.vue → botão "Fallback").
  async function loadTeamFallbackAgents(): Promise<TeamFallbackAgentRow[]> {
    const data = await post('list_team_fallback_agents')
    const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
    return rows.map(r => ({
      team_id:    Number(r.team_id),
      user_id:    Number(r.user_id),
      categories: parseJsonField<string[]>(r.categories as string | string[], []),
      fluxo_ids:  parseJsonField<number[]>(r.fluxo_ids as string | number[], []),
    }))
  }

  async function setTeamFallbackAgent(teamId: number, userId: number, categories: string[], fluxoIds: number[]): Promise<void> {
    await post('set_team_fallback_agent', { team_id: teamId, user_id: userId, categories, fluxo_ids: fluxoIds })
  }

  async function removeTeamFallbackAgent(teamId: number, userId: number): Promise<void> {
    await post('remove_team_fallback_agent', { team_id: teamId, user_id: userId })
  }

  // ── Últimos Acessos ────────────────────────────────────────────
  async function logAccess(userId: number, email: string, name: string): Promise<void> {
    await post('log_access', { user_id: userId, email, name })
  }

  async function loadLastAccess(): Promise<LastAccessRow[]> {
    const data = await post('list_last_access')
    const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
    return rows.map(r => ({
      user_id:     Number(r.user_id),
      last_access: Number(r.last_access),
    }))
  }

  // ── Status de disponibilidade (online/ocupado/offline) ─────────
  // Guarda o último status que o próprio agente escolheu no dropdown —
  // fonte confiável pra "Últimos Acessos"/aba Agentes, já que o
  // availability_status do Chatwoot (presença via ActionCable) fica
  // travado em offline pra quem só acessa via esta dashboard (ver v14).
  async function updateAgentStatus(userId: number, availability: string): Promise<void> {
    await post('update_agent_status', { user_id: userId, availability })
  }

  async function loadAgentStatus(): Promise<AgentStatusRow[]> {
    const data = await post('list_agent_status')
    const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
    return rows.map(r => ({
      user_id:      Number(r.user_id),
      availability: String(r.availability),
    }))
  }

  // ── Deslogar agente remotamente (Admin → Últimos Acessos) ──────
  // Fila de 1 pedido por usuário (migração v21). O polling de
  // check_force_logout nas abas do agente foi retirado (ver useForceLogout.ts).
  async function forceLogoutAgent(userId: number): Promise<void> {
    await post('force_logout_agent', { user_id: userId })
  }

  async function clearForceLogout(userId: number): Promise<void> {
    await post('clear_force_logout', { user_id: userId })
  }

  // ── Sessões abertas (Admin → Sessões — migração v30) ────────────
  // Diferente de Últimos Acessos (agent_access_log, só a data do último
  // login): aqui é uma sessão por ABA/navegador, com session_id gerado no
  // cliente (sessionStorage — ver useSessionTracking.ts) e heartbeat
  // periódico, pra dar visibilidade de sessões que ficaram abertas sem
  // fechar (aba/navegador fechado sem logout — last_seen_at para de
  // avançar) e permitir revogar uma sessão específica sem derrubar as
  // outras do mesmo agente (diferente de forceLogoutAgent, que é a conta
  // inteira).
  async function registerSession(sessionId: string, userId: number, email: string, name: string, device: string): Promise<void> {
    await postSessions('register_session', { session_id: sessionId, user_id: userId, email, name, device })
  }

  /** Atualiza last_seen_at e devolve se essa sessão foi revogada pelo admin. */
  async function heartbeatSession(sessionId: string): Promise<boolean> {
    const data = await postSessions('heartbeat_session', { session_id: sessionId })
    const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
    return rows.length > 0 && Number(rows[0].revoked) === 1
  }

  async function closeSession(sessionId: string): Promise<void> {
    await postSessions('close_session', { session_id: sessionId })
  }

  async function listSessions(): Promise<AgentSessionRow[]> {
    const data = await postSessions('list_sessions')
    const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
    return rows.map(r => ({
      session_id:   String(r.session_id),
      user_id:      Number(r.user_id),
      email:        String(r.email ?? ''),
      name:         String(r.name ?? ''),
      device:       String(r.device ?? ''),
      created_at:   Number(r.created_at),
      last_seen_at: Number(r.last_seen_at),
      revoked:      Number(r.revoked) === 1,
    }))
  }

  async function revokeSession(sessionId: string): Promise<void> {
    await postSessions('revoke_session', { session_id: sessionId })
  }

  /** "Continuar logado" numa sessão revogada — limpa só o revoked_at dela. */
  async function clearSessionRevoke(sessionId: string): Promise<void> {
    await postSessions('clear_session_revoke', { session_id: sessionId })
  }

  // ── Black List de contatos ──────────────────────────────────────
  // Guarda os contatos detectados com a etiqueta "lista_negra" — persistido
  // aqui (não só na etiqueta da conversa) porque uma conversa nova do mesmo
  // contato não carrega a etiqueta de novo (ver useBlacklist.ts, v15).
  async function loadBlacklist(): Promise<BlacklistRow[]> {
    const data = await post('list_blacklist')
    const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
    return rows.map(r => ({
      contact_id:   Number(r.contact_id),
      name:         String(r.name ?? ''),
      phone:        String(r.phone ?? ''),
      email:        String(r.email ?? ''),
      reason_label: String(r.reason_label ?? ''),
      context_team: String(r.context_team ?? ''),
      added_at:     Number(r.added_at),
    }))
  }

  async function addToBlacklist(entry: Omit<BlacklistRow, 'added_at'>): Promise<void> {
    await post('add_blacklist_contact', entry)
  }

  async function removeFromBlacklist(contactId: number): Promise<void> {
    await post('remove_blacklist_contact', { contact_id: contactId })
  }

  // Log de atribuição de conversa (call_conv) — movido pra
  // src/services/callConvService.ts, workflow isolado "Atribuicao-chamado"
  // (n8n/Atribuicao-chamado.json), separado deste (N8N_TEAMS_URL) pra isolar
  // debug de um caso em que os comandos não estavam gravando no workflow
  // gigante compartilhado.

  // ── Etiquetas protegidas (não podem ser removidas de uma conversa) ──
  // Chatwoot não tem esse conceito — guardado aqui pelo título (imutável
  // após a criação da etiqueta). Enforcement em conversationActionsService.ts.
  async function loadProtectedLabels(): Promise<string[]> {
    const data = await post('list_protected_labels')
    const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
    return rows.map(r => String(r.label_title))
  }

  async function protectLabel(title: string): Promise<void> {
    await post('protect_label', { title })
  }

  async function unprotectLabel(title: string): Promise<void> {
    await post('unprotect_label', { title })
  }

  return {
    dbLoading,
    dbError,
    loadProfiles,
    createProfile,
    updateProfile,
    deleteProfile,
    loadSettings,
    updateSetting,
    createTeam,
    updateTeam,
    deleteTeam,
    addTeamAgent,
    removeTeamAgent,
    createAgent,
    updateAgent,
    deleteAgent,
    loadAgentCategories,
    setAgentCategory,
    loadTeamFallbackAgents,
    setTeamFallbackAgent,
    removeTeamFallbackAgent,
    logAccess,
    loadLastAccess,
    updateAgentStatus,
    loadAgentStatus,
    forceLogoutAgent,
    clearForceLogout,
    registerSession,
    heartbeatSession,
    closeSession,
    listSessions,
    revokeSession,
    clearSessionRevoke,
    loadBlacklist,
    addToBlacklist,
    removeFromBlacklist,
    loadProtectedLabels,
    protectLabel,
    unprotectLabel,
  }
}
